import Dexie, { type Table } from 'dexie';

/**
 * OFFLINE KUYRUĞU (T1) — "offline-first zorunludur, sonradan eklenecek bir özellik değildir".
 *
 * ⚠️ TASARIM KARARI: kuyruk ÖN PLANDA ve GÖRÜNÜR çalışır; Background Sync API'ye
 * BAĞLI DEĞİLDİR.
 *
 * Gerekçe: Background Sync yalnız Chrome/Android'de var, iOS Safari'de YOK.
 * Ona bel bağlasaydık iOS'ta sessizce bozulurdu — sahadaki adam fotoğrafı çekti
 * sanır, gitmemiştir. Kanıt zincirinde bu kabul edilemez.
 *
 * Bunun yerine: uygulama açıldığında, öne geldiğinde ve bağlantı döndüğünde
 * otomatik denenir; ekranda kalıcı bir "N işlem bekliyor" göstergesi durur.
 * Davranış iOS ve Android'de BİREBİR AYNI olur.
 */

export interface BekleyenKomut {
  id?: number;
  /** Sunucu idempotency anahtarı — aynı komut iki kez giderse ikincisi no-op. */
  clientCommandId: string;
  yol: string;
  govde: Record<string, unknown>;
  /** Cihazda yakalanan GERÇEK olay zamanı. Sunucu saati offline'da yanlıştır. */
  olayZamani: string;
  denemeSayisi: number;
  sonHata: string | null;
  olusturmaZamani: number;
}

export interface BekleyenFoto {
  id?: number;
  jobId: string;
  step: string;
  blob: Blob;
  dosyaAdi: string;
  cekilmeZamani: string;
  denemeSayisi: number;
  sonHata: string | null;
}

class ModDb extends Dexie {
  komutlar!: Table<BekleyenKomut, number>;
  fotolar!: Table<BekleyenFoto, number>;

  constructor() {
    super('mod-erp');
    this.version(1).stores({
      komutlar: '++id, clientCommandId, olusturmaZamani',
      fotolar: '++id, jobId',
    });
  }
}

export const db = new ModDb();

export async function komutKuyrugaAl(
  yol: string,
  govde: Record<string, unknown>,
): Promise<void> {
  await db.komutlar.add({
    clientCommandId: (govde.clientCommandId as string) ?? crypto.randomUUID(),
    yol,
    govde,
    olayZamani: (govde.olayZamani as string) ?? new Date().toISOString(),
    denemeSayisi: 0,
    sonHata: null,
    olusturmaZamani: Date.now(),
  });
}

export async function fotoKuyrugaAl(f: Omit<BekleyenFoto, 'id' | 'denemeSayisi' | 'sonHata'>) {
  await db.fotolar.add({ ...f, denemeSayisi: 0, sonHata: null });
}

export async function bekleyenSayisi(): Promise<{ komut: number; foto: number }> {
  const [komut, foto] = await Promise.all([db.komutlar.count(), db.fotolar.count()]);
  return { komut, foto };
}

/**
 * Kuyruğu boşaltmaya çalışır. Komutlar SIRAYLA gider — adımların sırası önemli
 * (izin → kurulum → "kurdum"); paralel gönderim durum makinesini bozar.
 */
export async function kuyruguIsle(): Promise<{ gonderilen: number; kalan: number }> {
  if (!navigator.onLine) return { gonderilen: 0, kalan: await db.komutlar.count() };

  let gonderilen = 0;

  const komutlar = await db.komutlar.orderBy('olusturmaZamani').toArray();
  for (const k of komutlar) {
    try {
      const r = await fetch(`/api${k.yol}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(k.govde),
      });
      // 4xx = kural ihlali; tekrar denemek düzeltmez, kuyruktan çıkar ve hatayı sakla.
      // 5xx / ağ hatası = geçici, kuyrukta kalsın.
      if (r.ok || (r.status >= 400 && r.status < 500)) {
        if (!r.ok) {
          const g = (await r.json().catch(() => null)) as { hata?: string } | null;
          console.warn(`Komut reddedildi (${k.yol}):`, g?.hata);
        }
        await db.komutlar.delete(k.id!);
        gonderilen++;
      } else {
        await db.komutlar.update(k.id!, {
          denemeSayisi: k.denemeSayisi + 1,
          sonHata: `HTTP ${r.status}`,
        });
        break; // sıra bozulmasın
      }
    } catch (e) {
      await db.komutlar.update(k.id!, {
        denemeSayisi: k.denemeSayisi + 1,
        sonHata: e instanceof Error ? e.message : 'ağ hatası',
      });
      break;
    }
  }

  const fotolar = await db.fotolar.toArray();
  for (const f of fotolar) {
    try {
      const fd = new FormData();
      fd.append('dosya', f.blob, f.dosyaAdi);
      fd.append('step', f.step);
      fd.append('cekilmeZamani', f.cekilmeZamani);
      const r = await fetch(`/api/isler/${f.jobId}/foto`, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      });
      if (r.ok || (r.status >= 400 && r.status < 500)) {
        await db.fotolar.delete(f.id!);
        gonderilen++;
      } else {
        await db.fotolar.update(f.id!, { denemeSayisi: f.denemeSayisi + 1 });
      }
    } catch (e) {
      await db.fotolar.update(f.id!, {
        denemeSayisi: f.denemeSayisi + 1,
        sonHata: e instanceof Error ? e.message : 'ağ hatası',
      });
    }
  }

  return { gonderilen, kalan: (await db.komutlar.count()) + (await db.fotolar.count()) };
}
